<template>
    <div v-if="tabs.length" class="tabs-wrapper">
        <!-- Desktop view -->
        <ul class="nav nav-tabs d-none d-md-flex">
            <li v-for="tab in tabs" :key="tab.route" class="nav-item">
                <a
                    :class="{
                        active: isActiveRoute(tab.route),
                    }"
                    :data-testid="`tab-${tab.route.replaceAll('.', '-')}`"
                    :href="tab.href || '#'"
                    class="nav-link"
                    target="_self"
                    @click.prevent="handleTabClick(tab)"
                >
                    <div class="flex items-center gap-1">
                        <span :class="{ 'font-bold': tab.badge }" class="block truncate">{{ tab.label }}</span>
                        <exclamation-mark v-if="tab.badge" type="filled" />
                    </div>
                </a>
            </li>
        </ul>

        <!-- Mobile view -->
        <div class="d-md-none d-flex justify-content-end w-100">
            <dropdown :toggler-text="currentTabLabel" color="secondary">
                <dropdown-item
                    v-for="tab in tabs"
                    :key="tab.route"
                    :class="{ active: isActiveRoute(tab.route) }"
                    :href="tab.href || '#'"
                    class="dropdown-item"
                    @click.prevent="handleTabClick(tab)"
                >
                    <div class="flex items-center gap-1.5">
                        <span :class="{ 'font-bold': tab.badge }">{{ tab.label }}</span>
                        <exclamation-mark v-if="tab.badge" type="filled" />
                    </div>
                </dropdown-item>
            </dropdown>
        </div>
    </div>
</template>

<script setup>
import Dropdown from '@/Components/Dropdown.vue';
import DropdownItem from '@/Components/DropdownItem.vue';
import ExclamationMark from '../../icons/ExclamationMark.vue';
import { computed } from 'vue';

const props = defineProps({
    tabs: {
        type: Array,
        required: true,
        validator: (tabs) => {
            return tabs.every((tab) => typeof tab === 'object' && 'route' in tab && 'label' in tab);
        },
    },
    currentRoute: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['tab-click']);

const isActiveRoute = (route) => {
    return route === props.currentRoute;
};

const handleTabClick = (tab) => {
    emit('tab-click', tab);
};

const currentTab = computed(() => {
    return props.tabs.find((tab) => isActiveRoute(tab.route));
});

const currentTabLabel = computed(() => {
    return currentTab.value?.label || props.tabs[0]?.label || '';
});
</script>

<style scoped>
.tabs-wrapper {
    margin-bottom: 1rem;
}

.nav-tabs-dropdown {
    background: #fff;
    border: 1px solid var(--c-border-color);
    border-radius: var(--c-border-radius);
    padding: 0.875rem 1.25rem;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1rem;
    color: var(--c-body-color);
    width: 100%;
}

.btn-group {
    width: 100%;
    text-align: left;
}

:deep(.dropdown-toggle) {
    justify-content: start !important;
    background-color: #fff !important;
}
:deep(.dropdown-toggle::after) {
    right: 10px;
    top: calc(50% - 4px);
}

:deep(.dropdown-menu) {
    width: 100%;
}

.dropdown-item {
    display: block;
    padding: 0.875rem 1.25rem;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1rem;
    color: var(--c-body-color);
    text-decoration: none;
}

.dropdown-item.active {
    background-color: var(--c-gray-light);
    color: var(--c-body-color);
}

.dropdown-item:hover,
.dropdown-item:focus {
    background-color: var(--c-gray-light);
    color: var(--c-body-color);
}
</style>
